import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Users, CalendarCheck, Plus, LogOut, ShieldCheck } from "lucide-react";
import { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [tenants, setTenants] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", owner_name: "", owner_email: "", owner_password: "" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    API.get("/admin/overview").then((r) => setOverview(r.data)).catch((e) => toast.error(formatApiError(e)));
    API.get("/admin/tenants").then((r) => setTenants(r.data)).catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => {
    if (user?.is_platform_admin) load();
  }, [user]);

  if (!user?.is_platform_admin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50" data-testid="admin-denied">
        <p className="text-sm text-slate-500">Accès réservé à l'administrateur de la plateforme.</p>
      </div>
    );
  }

  const createTenant = async () => {
    setSaving(true);
    try {
      await API.post("/admin/tenants", { ...form, owner_email: form.owner_email || null });
      toast.success("Entreprise créée");
      setOpen(false);
      setForm({ name: "", slug: "", owner_name: "", owner_email: "", owner_password: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (t) => {
    try {
      await API.patch(`/admin/tenants/${t.id}`, { active: !t.active });
      toast.success(t.active ? "Entreprise désactivée" : "Entreprise activée");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-page">
      <header className="flex h-16 items-center justify-between border-b border-slate-200 bg-white px-4 sm:px-8">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900">
            <ShieldCheck className="h-4 w-4 text-white" />
          </div>
          <span className="font-display text-lg font-bold text-slate-900">Administration plateforme</span>
        </div>
        <Button variant="ghost" size="sm" onClick={async () => { await logout(); navigate("/login"); }} data-testid="btn-admin-logout">
          <LogOut className="mr-2 h-4 w-4" /> Déconnexion
        </Button>
      </header>

      <main className="mx-auto max-w-6xl p-4 sm:p-8">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Kpi icon={Building2} label="Entreprises" value={overview?.tenants_count} testid="admin-kpi-tenants" />
          <Kpi icon={Users} label="Utilisateurs" value={overview?.users_count} testid="admin-kpi-users" />
          <Kpi icon={CalendarCheck} label="RDV total" value={overview?.bookings_count} testid="admin-kpi-bookings" />
          <Kpi icon={CalendarCheck} label="RDV créés aujourd'hui" value={overview?.bookings_today} testid="admin-kpi-bookings-today" />
        </div>

        <div className="mt-8 flex items-center justify-between">
          <h2 className="font-display text-xl font-bold text-slate-900">Entreprises</h2>
          <Button onClick={() => setOpen(true)} data-testid="btn-admin-new-tenant"><Plus className="mr-2 h-4 w-4" /> Nouvelle entreprise</Button>
        </div>

        <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full min-w-[720px] text-sm" data-testid="admin-tenants-table">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                <th className="px-4 py-3">Entreprise</th>
                <th className="px-4 py-3">Lien public</th>
                <th className="px-4 py-3">Utilisateurs</th>
                <th className="px-4 py-3">RDV</th>
                <th className="px-4 py-3">Créée le</th>
                <th className="px-4 py-3">Active</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {tenants === null ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Chargement...</td></tr>
              ) : tenants.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400" data-testid="admin-tenants-empty">Aucune entreprise.</td></tr>
              ) : (
                tenants.map((t) => (
                  <tr key={t.id} data-testid={`admin-tenant-row-${t.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{t.name}</td>
                    <td className="px-4 py-3">
                      <a href={`/book/${t.slug}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline" data-testid={`admin-tenant-link-${t.slug}`}>
                        /book/{t.slug}
                      </a>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{t.users_count}</td>
                    <td className="px-4 py-3 text-slate-600">{t.bookings_count}</td>
                    <td className="px-4 py-3 text-slate-500">{new Date(t.created_at).toLocaleDateString("fr-FR")}</td>
                    <td className="px-4 py-3">
                      <Switch checked={t.active} onCheckedChange={() => toggleActive(t)} data-testid={`admin-tenant-active-${t.id}`} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="admin-tenant-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Nouvelle entreprise</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom de l'entreprise</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-tenant-name" className="mt-1" />
            </div>
            <div>
              <Label>Identifiant public (facultatif)</Label>
              <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} data-testid="admin-tenant-slug" className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email du propriétaire</Label>
                <Input type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} data-testid="admin-owner-email" className="mt-1" />
              </div>
              <div>
                <Label>Mot de passe</Label>
                <Input type="password" value={form.owner_password} onChange={(e) => setForm({ ...form, owner_password: e.target.value })} data-testid="admin-owner-password" className="mt-1" />
              </div>
            </div>
            <div>
              <Label>Nom du propriétaire</Label>
              <Input value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} data-testid="admin-owner-name" className="mt-1" />
            </div>
            <Button className="w-full" onClick={createTenant} disabled={!form.name || saving} data-testid="admin-tenant-submit">
              {saving ? "Création..." : "Créer l'entreprise"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-400">
        <Icon className="h-4 w-4" />
        <span className="text-xs font-medium uppercase tracking-wider">{label}</span>
      </div>
      <p className="mt-2 font-display text-3xl font-bold text-slate-900">{value ?? "—"}</p>
    </div>
  );
}
